import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import {
  Activity,
  KeyRound,
  MonitorPlay,
  RadioTower,
  Box,
  ArrowLeft,
  BarChart3,
  Bot,
  Brain,
  Building2,
  ChevronDown,
  Coins,
  Image as ImageIcon,
  PanelLeft,
  PanelLeftClose,
  ClipboardList,
  ScrollText,
  Cog,
  Eye,
  FlaskConical,
  Bug,
  LayoutDashboard,
  LogOut,
  Mail,
  MapPin,
  Megaphone,
  Menu,
  Smartphone,
  Moon,
  Settings,
  Route,
  ShieldCheck,
  Stethoscope,
  Sun,
  User,
  UserCog,
  Users,
  Video,
  X, BellRing, ScanFace, ChartLine } from 'lucide-react';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../hooks/useTheme';
import { Avatar } from '../ui/Avatar';
import { Modal } from '../ui/Modal';
import { EnvironmentBadge } from '../ui/EnvironmentBadge';
import { Button } from '../ui/Button';
import { NotificationBell } from './NotificationBell';
import { AdminAssistant } from '../assistant/AdminAssistant';
import { ReportarProblema, type ReportarProblemaHandle } from '../report/ReportarProblema';
import styles from './MainLayout.module.css';

interface MainLayoutProps {
  children: ReactNode;
  pageTitle?: string;
  /** Descripción corta bajo el título de la página. */
  pageSubtitle?: string;
  /** Si se provee, muestra un botón de regresar (icono) antes del título. */
  onBack?: () => void;
  breadcrumb?: string;
  roomInfo?: { name: string; waiting: number };
}

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  badge?: string;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

function getCrumbLabel(pathname: string): string {
  if (pathname.startsWith('/salas')) return 'Salas de atención';
  if (pathname.startsWith('/sesiones')) return 'Sesiones activas';
  if (pathname.startsWith('/historial')) return 'Historial';
  if (pathname.startsWith('/consulta/')) return 'Consulta en curso';
  if (pathname.startsWith('/perfil')) return 'Mi perfil';
  if (pathname.startsWith('/config/salas')) return 'Configuración de salas';
  // /admin/pre-registro redirects to /admin/medicos; keep the matcher here so
  // the breadcrumb resolves correctly during the route transition.
  if (pathname.startsWith('/admin/dashboard')) return 'Dashboard';
  if (pathname.startsWith('/admin/piso-3d')) return 'Piso 3D';
  if (pathname.startsWith('/admin/monitor')) return 'Monitor en vivo';
  if (pathname.startsWith('/admin/pre-registro')) return 'Gestión de usuarios';
  if (pathname.startsWith('/admin/medicos')) return 'Gestión de usuarios';
  if (pathname.startsWith('/admin/configuracion')) return 'Configuración global';
  if (pathname.startsWith('/admin/ai-prompts')) return 'Prompts IA';
  if (pathname.startsWith('/admin/triage-prompt')) return 'Prompts IA';
  if (pathname.startsWith('/admin/config-ia')) return 'Configuración de IA';
  if (pathname.startsWith('/admin/uso-ia')) return 'Uso de IA';
  if (pathname.startsWith('/admin/fondos-virtuales')) return 'Fondos Virtuales';
  if (pathname.startsWith('/admin/regiones-teleconsulta')) return 'Regiones de teleconsulta';
  if (pathname.startsWith('/admin/pruebas')) return 'Pruebas manuales';
  if (pathname.startsWith('/admin/seguridad')) return 'Seguridad de acceso';
  if (pathname.startsWith('/admin/auditoria-accesos')) return 'Auditoría de Accesos';
  if (pathname.startsWith('/admin/auditoria')) return 'Auditoría';
  if (pathname.startsWith('/admin/correo')) return 'Servicio de Correo';
  if (pathname.startsWith('/admin/registro-biometrico')) return 'Registro biométrico';
  if (pathname.startsWith('/admin/uso-app')) return 'Uso de la app';
  if (pathname.startsWith('/notificaciones/pruebas')) return 'Dispositivos de prueba';
  if (pathname.startsWith('/notificaciones/nueva')) return 'Nueva campaña';
  if (pathname.startsWith('/notificaciones')) return 'Campañas de notificación';
  return 'Portal';
}

export default function MainLayout({
  children,
  pageTitle,
  pageSubtitle,
  onBack,
  breadcrumb,
  roomInfo,
}: MainLayoutProps) {
  const { user, logout, isAdmin, isSupervisor, isMedico, isNotificador, isVisor } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const reporteRef = useRef<ReportarProblemaHandle>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar-collapsed') === '1'; } catch { return false; }
  });
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem('sidebar-collapsed', next ? '1' : '0'); } catch { /* noop */ }
      return next;
    });
  }, []);

  const displayName = user?.displayName || user?.email || 'Usuario';

  // Foto de perfil: Auth photoURL es la fuente primaria, pero cuando la foto la
  // establece un ADMIN (profileService.setMedicoAvatar) solo se escribe
  // medicos/{uid}.avatarUrl — el Auth del usuario queda sin photoURL. Fallback
  // best-effort a ese doc para que la foto se vea igual en el topbar.
  const [fotoDocFallback, setFotoDocFallback] = useState<string | null>(null);
  useEffect(() => {
    let cancelado = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!user || user.photoURL) { setFotoDocFallback(null); return; }
    getDoc(doc(db, 'medicos', user.uid))
      .then((snap) => {
        if (!cancelado) setFotoDocFallback((snap.data()?.avatarUrl as string | undefined) ?? null);
      })
      .catch(() => { if (!cancelado) setFotoDocFallback(null); });
    return () => { cancelado = true; };
  }, [user]);
  const fotoURL = user?.photoURL || fotoDocFallback;

  const handleLogout = async () => {
    setShowLogoutModal(false);
    await logout();
  };

  // ─── Navigation groups ─────────────────────────────────────

  const navGroups: NavGroup[] = [];

  // Secciones visibles para Administrador (admin) y Supervisor. Diferencias:
  //  • Administrador: Usuarios, Inteligencia Artificial, Auditoría, Pruebas —
  //    pero NO Configurar salas/Configuración/Personalización (exclusivas del
  //    Supervisor) ni las pantallas clínicas (Salas / Historial).
  //  • Supervisor: Usuarios, Configurar salas, Configuración, Personalización,
  //    Auditoría; NO Inteligencia Artificial ni Pruebas; sí ve Salas e Historial.
  // El rol VISOR no tenía menú: entraba directo a /tv y no había forma de
  // llegar a ninguna otra pantalla. Ahora ve las dos de solo lectura que le
  // corresponden — la trazabilidad y el tablero de televisor.
  if (isVisor) {
    navGroups.push({
      title: 'Monitoreo',
      items: [
        { to: '/admin/trazabilidad', label: 'Trazabilidad', icon: Route },
        { to: '/tv/operacion',       label: 'Pantalla TV',  icon: MonitorPlay },
      ],
    });
  }

  if (isAdmin || isSupervisor) {
    // ── Monitoreo ──
    navGroups.push({
      title: 'Monitoreo',
      items: [
        { to: '/admin/dashboard', label: 'Dashboard',        icon: BarChart3 },
        { to: '/admin/monitor',   label: 'Monitor en vivo',  icon: RadioTower },
        { to: '/admin/piso-3d',   label: 'Piso 3D',          icon: Box },
        // Las alertas van ANTES del monitor: son lo que hay que atender; el
        // monitor es para investigar después.
        { to: '/admin/alertas',   label: 'Alertas', icon: BellRing },
        { to: '/admin/eventos',   label: 'Monitor de eventos', icon: ScrollText },
        { to: '/admin/trazabilidad', label: 'Trazabilidad', icon: Route },
        { to: '/sesiones',        label: 'Sesiones activas', icon: Video },
        { to: '/supervision',     label: 'Supervisión',      icon: Eye },
        // Abre la pantalla de televisor: sale del layout del portal y rota sola.
        { to: '/tv/operacion',    label: 'Pantalla TV',      icon: MonitorPlay },
      ],
    });

    // ── Gestión ──
    // "Usuarios" es de admin+supervisor; "Configurar salas" y "Configuración"
    // son EXCLUSIVAS del Supervisor (el Administrador ya no opera salas ni
    // configuración global — ver también gestionSalas.ts/firestore.rules).
    navGroups.push({
      title: 'Gestión',
      items: [
        { to: '/admin/medicos', label: 'Usuarios', icon: Users },
        ...(isSupervisor
          ? [
              { to: '/config/salas',        label: 'Configurar salas', icon: Building2 },
              { to: '/admin/configuracion', label: 'Configuración',   icon: Cog },
            ]
          : []),
      ],
    });

    // ── Inteligencia Artificial — exclusivo del Administrador ──
    if (isAdmin) {
      navGroups.push({
        title: 'Inteligencia Artificial',
        items: [
          { to: '/admin/ai-prompts', label: 'Prompts IA',   icon: Bot },
          { to: '/admin/config-ia',  label: 'Configurar IA', icon: Brain },
          { to: '/admin/uso-ia',     label: 'Uso de IA',    icon: Coins },
        ],
      });
    }

    // ── Personalización — exclusivo del Supervisor ──
    if (isSupervisor) {
      navGroups.push({
        title: 'Personalización',
        items: [
          { to: '/admin/fondos-virtuales',      label: 'Fondos Virtuales',       icon: ImageIcon },
          { to: '/admin/regiones-teleconsulta', label: 'Regiones Teleconsulta',  icon: MapPin },
        ],
      });
    }

    // ── Auditoría ──
    navGroups.push({
      title: 'Auditoría',
      items: [
        { to: '/admin/seguridad',         label: 'Seguridad de acceso', icon: KeyRound },
        { to: '/admin/auditoria',         label: 'Auditoría',         icon: ShieldCheck },
        { to: '/admin/auditoria-accesos', label: 'Auditoría Accesos', icon: Activity },
      ],
    });

    // ── Pruebas manuales — exclusivo del Administrador ──
    if (isAdmin) {
      navGroups.push({
        title: 'Pruebas',
        items: [
          { to: '/admin/pruebas', label: 'Pruebas manuales', icon: FlaskConical },
        ],
      });
    }
  }

  // ─── Notificaciones — campañas push (Admin + Notificador) ──
  // Para el rol notificador este es el ÚNICO grupo del menú.
  if (isAdmin || isNotificador) {
    navGroups.push({
      title: 'Notificaciones',
      items: [
        { to: '/notificaciones',         label: 'Campañas',              icon: Megaphone },
        { to: '/notificaciones/pruebas', label: 'Dispositivos de prueba', icon: Smartphone },
      ],
    });
  }

  // ─── Servicio de Correo — administración del servicio SES (GPP-202603) ──
  // Exclusivo del Administrador. Es distinto de «Notificaciones» (push): aquí se ven las
  // estadísticas, reportes y catálogos del correo transaccional centralizado.
  if (isAdmin) {
    navGroups.push({
      title: 'Servicio de Correo',
      items: [
        { to: '/admin/correo', label: 'Servicio de Correo', icon: Mail },
      ],
    });
  }

  // ─── App móvil: uso y registro biométrico — exclusivo del Administrador ──
  if (isAdmin) {
    navGroups.push({
      title: 'App móvil',
      items: [
        { to: '/admin/uso-app', label: 'Uso de la app', icon: ChartLine },
        { to: '/admin/registro-biometrico', label: 'Registro biométrico', icon: ScanFace },
      ],
    });
  }

  // ─── Atención clínica ─────────────────────────────────────
  // Supervisor y Médico. El Administrador NO atiende pacientes (sin Salas/Historial).
  if (isSupervisor || isMedico) {
    navGroups.push({
      title: 'Atención clínica',
      items: [
        // Médico: su propio dashboard con indicadores filtrados a él.
        ...(isMedico
          ? [{ to: '/dashboard', label: 'Dashboard', icon: BarChart3 }]
          : []),
        { to: '/salas',     label: 'Salas',           icon: LayoutDashboard },
        { to: '/historial', label: 'Historial',       icon: ClipboardList },
      ],
    });
  }

  const crumbCurrent = breadcrumb || getCrumbLabel(location.pathname);

  return (
    <div className={`${styles.shell} ${collapsed ? styles.shellCollapsed : ''}`}>
      {/* ─── Sidebar ──────────────────────────────────── */}
      <aside
        className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ''} ${collapsed ? styles.sidebarCollapsed : ''}`}
        aria-label="Navegación principal"
      >
        <div className={styles.sidebarBrand}>
          {/* Logo MINSAlud PdP — ocupa toda la sección (colapsado → solo ícono) */}
          <img className={styles.brandLogo} src="/minsalud-logo.png" alt="MINSAlud — Portal del Paciente" />
          <img className={styles.brandIcon} src="/minsalud-icon.png" alt="MINSAlud" />
          <button
            type="button"
            className={styles.closeSidebarBtn}
            onClick={() => setSidebarOpen(false)}
            aria-label="Cerrar menú"
          >
            <X size={16} />
          </button>
        </div>

        <nav className={styles.sidebarNav}>
          {navGroups.map((group) => (
            <div key={group.title} className={styles.navGroup}>
              <span className={styles.navGroupTitle}>{group.title}</span>
              <ul className={styles.navList}>
                {group.items.map(({ to, label, icon: Icon, badge }) => (
                  <li key={to}>
                    <NavLink
                      to={to}
                      className={({ isActive }) =>
                        `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
                      }
                      onClick={() => setSidebarOpen(false)}
                    >
                      <Icon size={16} className={styles.navIcon} aria-hidden="true" />
                      <span className={styles.navLabel}>{label}</span>
                      {badge && <span className={styles.navBadge}>{badge}</span>}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className={styles.sidebarFoot}>
          <NavLink
            to="/perfil"
            className={({ isActive }) =>
              `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <UserCog size={16} className={styles.navIcon} aria-hidden="true" />
            <span className={styles.navLabel}>Mi perfil</span>
          </NavLink>
        </div>
      </aside>

      {/* Mobile overlay */}
      <div
        className={`${styles.overlay} ${sidebarOpen ? styles.overlayVisible : ''}`}
        onClick={() => setSidebarOpen(false)}
        aria-hidden="true"
      />

      {/* ─── Main column ──────────────────────────────── */}
      <div className={styles.main}>
        {/* Top bar */}
        <header className={styles.topbar}>
          <div className={styles.topbarLeft}>
            <button
              type="button"
              className={styles.hamburger}
              onClick={() => setSidebarOpen((v) => !v)}
              aria-label="Abrir menú"
            >
              <Menu size={18} />
            </button>

            <button
              type="button"
              className={styles.collapseBtn}
              onClick={toggleCollapsed}
              aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
              title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
            >
              {collapsed
                ? <PanelLeft size={16} aria-hidden="true" />
                : <PanelLeftClose size={16} aria-hidden="true" />}
            </button>

            <nav className={styles.crumb} aria-label="Breadcrumb">
              <span className={styles.crumbHome}>Portal</span>
              <span className={styles.crumbSep} aria-hidden="true">/</span>
              <span className={styles.crumbCurrent}>{crumbCurrent}</span>
            </nav>

            <EnvironmentBadge />

            {roomInfo && (
              <div className={styles.roomPill}>
                <span className={styles.livePulse} aria-hidden="true" />
                <span className={styles.roomName}>{roomInfo.name}</span>
                <span className={styles.roomSep}>·</span>
                <span className={styles.roomCount}>
                  {roomInfo.waiting} en espera
                </span>
              </div>
            )}
          </div>

          <div className={styles.topbarRight}>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={() => reporteRef.current?.abrir()}
              aria-label="Reportar un problema, mejora o requerimiento"
              title="Reportar un problema"
            >
              <Bug size={16} />
            </button>

            <button
              type="button"
              className={styles.iconBtn}
              onClick={toggleTheme}
              aria-label={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
              title={isDark ? 'Modo claro' : 'Modo oscuro'}
            >
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            {/* Notificaciones de teleconsulta: SOLO médico y supervisor. Se
                gatea en el montaje (no dentro del componente) para que los
                demás roles ni siquiera abran la suscripción a `consultas`. */}
            {(isMedico || isSupervisor) && (
              <NotificationBell buttonClassName={styles.iconBtn} />
            )}

            <div className={styles.profileWrap}>
              <button
                type="button"
                className={styles.profileBtn}
                onClick={() => setProfileOpen((v) => !v)}
                aria-expanded={profileOpen}
              >
                <Avatar className={styles.avatar} nombre={displayName} fotoURL={fotoURL} />
                <div className={styles.profileMeta}>
                  <span className={styles.profileName}>{displayName}</span>
                </div>
                <ChevronDown size={14} className={styles.profileChev} />
              </button>

              {profileOpen && (
                <>
                  <div
                    className={styles.profileOverlay}
                    onClick={() => setProfileOpen(false)}
                  />
                  <div className={styles.profileDrop} role="menu">
                    <div className={styles.profileDropHead}>
                      <Avatar className={styles.avatarLg} nombre={displayName} fotoURL={fotoURL} />
                      <div>
                        <div className={styles.profileDropName}>{displayName}</div>
                        <div className={styles.profileDropEmail}>{user?.email}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className={styles.dropItem}
                      onClick={() => { setProfileOpen(false); navigate('/perfil'); }}
                    >
                      <User size={15} /> Mi perfil
                    </button>
                    <button
                      type="button"
                      className={styles.dropItem}
                      onClick={() => { setProfileOpen(false); navigate('/salas'); }}
                    >
                      <Stethoscope size={15} /> Mis salas
                    </button>
                    {isSupervisor && (
                      <button
                        type="button"
                        className={styles.dropItem}
                        onClick={() => { setProfileOpen(false); navigate('/admin/configuracion'); }}
                      >
                        <Settings size={15} /> Configuración
                      </button>
                    )}
                    <div className={styles.dropDivider} />
                    <button
                      type="button"
                      className={`${styles.dropItem} ${styles.dropItemDanger}`}
                      onClick={() => { setProfileOpen(false); setShowLogoutModal(true); }}
                    >
                      <LogOut size={15} /> Cerrar sesión
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Optional page header */}
        {pageTitle && (
          <div className={styles.pageHeader}>
            <div className={styles.pageHeaderMain}>
              {onBack && (
                <button
                  type="button"
                  className={styles.pageBackBtn}
                  onClick={onBack}
                  aria-label="Regresar"
                  title="Regresar"
                >
                  <ArrowLeft size={18} aria-hidden="true" />
                </button>
              )}
              <div className={styles.pageTitleWrap}>
                <h1 className={styles.pageTitle}>{pageTitle}</h1>
                {pageSubtitle && <p className={styles.pageSubtitle}>{pageSubtitle}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <main className={styles.content}>{children}</main>
      </div>

      {/* Logout modal */}
      <Modal
        open={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        title="Cerrar sesión"
      >
        <div className={styles.logoutModal}>
          <p className={styles.logoutModalTitle}>¿Está seguro?</p>
          <p className={styles.logoutModalText}>
            Cerrará su sesión y deberá iniciarla nuevamente con sus credenciales
            institucionales.
          </p>
          <div className={styles.logoutModalActions}>
            <Button variant="secondary" onClick={() => setShowLogoutModal(false)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleLogout}>
              Cerrar sesión
            </Button>
          </div>
        </div>
      </Modal>

      {/* Asistente IA de configuración (salas/horarios/médicos). Disponible para
          Administrador y Supervisor; NO modifica prompts ni configuración de IA. */}
      {(isAdmin || isSupervisor) && <AdminAssistant />}

      {/* Reporte estandarizado de errores/mejoras/requerimientos → issue de GitHub.
          Disponible para TODOS los usuarios autenticados (cualquier tester puede reportar). */}
      <ReportarProblema ref={reporteRef} />
    </div>
  );
}
